import { Injectable, inject, Inject, PLATFORM_ID } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { tap, catchError, retry, timeout, map } from 'rxjs/operators';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Router } from '@angular/router';
import { isPlatformBrowser } from '@angular/common';
import { SHA256, enc } from 'crypto-js';

import { SecureEncryptionService, EncryptionResult } from './secure-encryption-service';
import { BrowserStorageService } from './browser-storage-service';
import { SessionExpiredService } from './session-expired-service';
import { UniversalBase64 } from '../utils/universal-base64.util';
import { environment } from '../../../environments/environment';

export interface SecureLoginCredentials {
  email: string;
  password: string;
  deviceFingerprint?: string;
  captchaToken?: string;
  encryptionBypass?: boolean;  // TEMPORARY: Enable encryption bypass for development
}

export interface LoginAttemptInfo {
  attempts: number;
  lastAttempt: number;
  lockedUntil?: number;
}


@Injectable({
  providedIn: 'root'
})
export class SecureAuthService {
  private readonly endpoint = `${environment.api}`;
  private readonly MAX_LOGIN_ATTEMPTS = environment.maxLoginAttempts || 5;
  private readonly LOCKOUT_DURATION = (environment.lockoutDurationMinutes || 15) * 60 * 1000; // Convert minutes to ms
  private readonly SESSION_TIMEOUT = (environment.sessionTimeoutMinutes || 30) * 60 * 1000; // Convert minutes to ms
  private readonly REQUEST_TIMEOUT = (environment.requestTimeoutSeconds || 15) * 1000; // 15s timeout
  
  private http = inject(HttpClient);
  private router = inject(Router);
  private secureEncryption = inject(SecureEncryptionService);
  private sessionExpiredService = inject(SessionExpiredService);
  private jwtHelper = new JwtHelperService();

  private authState = new BehaviorSubject<boolean>(false);
  private sessionTimer: any;
  private activityAbortController: AbortController | null = null;
  private activityLastResetAt: number = 0;
  private readonly activityThrottleMs: number = 1000;
  private cachedDeviceFingerprint: string | null = null;


  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private browserStorage: BrowserStorageService
  ) {
    this.checkExistingSession();
  }

  /**
   * Secure login with client-side encryption
   */
  secureLogin(credentials: SecureLoginCredentials): Observable<any> {
    // Check if account is locked
    if (this.isAccountLocked(credentials.email)) {
      const lockInfo = this.getLoginAttemptInfo(credentials.email);
      const remainingTime = Math.ceil((lockInfo.lockedUntil! - Date.now()) / 60000);
      return throwError(`Account locked. Try again in ${remainingTime} minutes.`);
    }

    // TEMPORARY: Check for encryption bypass mode
    const useEncryptionBypass = (credentials as any).encryptionBypass === true;
    
    if (useEncryptionBypass) {
      return this.secureLoginWithBypass(credentials);
    }

    // Generate device fingerprint
    const deviceFingerprint = this.generateDeviceFingerprint();

    // Create observable that handles async encryption
    return new Observable(observer => {
      // Encrypt password client-side (async operation)
      this.secureEncryption.encryptPassword(
        credentials.password,
        credentials.email // Use email as user context for key separation
      ).then(async (encryptionResult) => {
        // Create flat payload structure expected by backend
        const encryptedPayloadData = {
          encrypted_password: encryptionResult.encryptedData,
          salt: encryptionResult.salt,
          iv: encryptionResult.iv,
          timestamp: encryptionResult.timestamp,
          device_fingerprint: deviceFingerprint,
          key_version: encryptionResult.keyId
        };
        
        const base64Payload = UniversalBase64.encode(JSON.stringify(encryptedPayloadData));
        
        const securePayload = {
          email: credentials.email,
          encrypted_payload: base64Payload,
          device_fingerprint: deviceFingerprint,
          captcha_token: credentials.captchaToken,
          client_timestamp: Date.now(),
          security_headers: this.generateSecurityHeaders()
        };

        const headers = this.getSecureHeaders();

        // Make HTTP request
        this.http.post(`${this.endpoint}accounts/secure-admin-login/`, securePayload, {
          headers: headers
        }).pipe(
          timeout(this.REQUEST_TIMEOUT),
          retry(1), // Reduced retry attempts for better error handling
          map(response => this.transformResponse(response)),
          tap(response => {
            if (response.success) {
              this.handleSuccessfulLogin(response, credentials.email);
            }
          }),
          catchError(error => {
            return this.handleLoginError(error, credentials.email);
          })
        ).subscribe({
          next: (response) => observer.next(response),
          error: (error) => observer.error(error),
          complete: () => observer.complete()
        });
      }).catch(error => {
        observer.error('Password encryption failed');
      });
    });
  }

  /**
   * Transform backend response to format expected by sign-in component
   */
  private transformResponse(response: any): any {
    // If the response has the expected format, return it as is
    if (response.success !== undefined) {
      return response;
    }

    // Transform the backend response to match the expected format
    if (response.status === 200 && response.valid_or_not) {
      const transformedResponse = {
        success: true,
        status: response.status,
        data: {
          is_temporary_password_changed: response.is_temporary_password_changed,
          str_for: response.str_for,
          all_params: response.all_params,
          valid_or_not: response.valid_or_not,
          tenant: response.tenant,
          message: response.message,
          user_type: response.user_type,
          user_timezone: response.user_timezone,
          // Include tokens for secure auth
          access_token: response.access_token,
          refresh_token: response.refresh_token,
          jwt: response.jwt,
          refresh: response.refresh
        }
      };
      return transformedResponse;
    } else if (response.status === 203) {
      const transformedResponse = {
        success: false,
        status: response.status,
        data: {
          email: response.email,
          is_temporary_password_changed: response.is_temporary_password_changed
        },
        message: response.message
      };
      return transformedResponse;
    } else {
      // Handle error responses
      const errorResponse = {
        success: false,
        status: response.status || 400,
        message: response.message || 'Login failed',
        error: response.error || 'Authentication error'
      };
      return errorResponse;
    }
  }

    /**
     * TEMPORARY: Secure login with encryption bypass
     */
    private secureLoginWithBypass(credentials: SecureLoginCredentials): Observable<any> {
        // Create a simple payload that works with sample decryption
        const simplePayload = {
            password: credentials.password,
            email: credentials.email,
            timestamp: Date.now(),
            device_fingerprint: this.generateDeviceFingerprint()
        };
        
        // Base64 encode the simple payload using browser native btoa
        const payloadJson = JSON.stringify(simplePayload);
        const encodedPayload = btoa(payloadJson);
        
        const requestPayload = {
            email: credentials.email,
            encrypted_payload: encodedPayload
        };

        const headers = this.getSecureHeaders();
        
        return this.http.post<any>(`${this.endpoint}accounts/secure-admin-login/`, requestPayload, { headers }).pipe(
            timeout(this.REQUEST_TIMEOUT),
            // First normalize the backend response to our expected shape
            map(response => this.transformResponse(response)),
            // Then handle successful login using the normalized response
            tap(response => {
                if (response?.success === true) {
                    this.handleSuccessfulLogin(response, credentials.email);
                }
            }),
            // No retry — fail fast on login
            catchError(error => {
                return this.handleLoginError(error, credentials.email);
            })
        );
    }

  /**
   * Handle successful login
   * NOTE: Session data (app_params, loggedIn, tenant, etc.) is stored by the
   * sign-in component after this handler runs. This method only handles
   * auth-service-specific state to avoid double work and expensive crypto.
   */
  private handleSuccessfulLogin(response: any, email: string): void {
    // Clear login attempts
    this.clearLoginAttempts(email);

    // Set minimal session info
    this.browserStorage.setSessionItem('loginTime', Date.now().toString());

    // Defer session monitoring so it doesn't block the login redirect
    setTimeout(() => this.startSessionMonitoring(), 100);

    // Update auth state
    this.authState.next(true);
  }

  /**
   * Handle login error
   */
  private handleLoginError(error: any, email: string): Observable<never> {
    // Record failed attempt
    this.recordFailedLoginAttempt(email);
    return throwError(error);
  }

  /**
   * Check if account is locked
   */
  private isAccountLocked(email: string): boolean {
    const attemptInfo = this.getLoginAttemptInfo(email);
    
    if (attemptInfo.lockedUntil && Date.now() < attemptInfo.lockedUntil) {
      return true;
    }

    if (attemptInfo.attempts >= this.MAX_LOGIN_ATTEMPTS) {
      // Lock account
      attemptInfo.lockedUntil = Date.now() + this.LOCKOUT_DURATION;
      this.storeLoginAttemptInfo(email, attemptInfo);
      return true;
    }

    return false;
  }

  /**
   * Record failed login attempt
   */
  private recordFailedLoginAttempt(email: string): void {
    const attemptInfo = this.getLoginAttemptInfo(email);
    attemptInfo.attempts++;
    attemptInfo.lastAttempt = Date.now();
    
    this.storeLoginAttemptInfo(email, attemptInfo);
  }

  /**
   * Clear login attempts
   */
  private clearLoginAttempts(email: string): void {
    this.browserStorage.removeSessionItem(`login_attempts_${UniversalBase64.encode(email)}`);
  }

  /**
   * Get login attempt info
   */
  private getLoginAttemptInfo(email: string): LoginAttemptInfo {
    try {
      const stored = this.browserStorage.getSessionItem(`login_attempts_${UniversalBase64.encode(email)}`);
      
      if (!stored || stored === 'undefined' || stored.trim() === '') {
        return { attempts: 0, lastAttempt: 0 };
      }
      
      return JSON.parse(stored);
    } catch {
      return { attempts: 0, lastAttempt: 0 };
    }
  }

  /**
   * Store login attempt info
   */
  private storeLoginAttemptInfo(email: string, info: LoginAttemptInfo): void {
    this.browserStorage.setSessionItem(`login_attempts_${UniversalBase64.encode(email)}`, JSON.stringify(info));
  }

  /**
   * Generate device fingerprint matching backend expectations.
   * Cached after first computation — device properties don't change mid-session.
   */
  private generateDeviceFingerprint(): string {
    if (this.cachedDeviceFingerprint) {
      return this.cachedDeviceFingerprint;
    }

    const fingerprint_data = {
      'language': navigator.language,
      'screen_resolution': `${screen.width}x${screen.height}`,
      'timezone': Intl.DateTimeFormat().resolvedOptions().timeZone,
      'user_agent': navigator.userAgent
    };
    
    const fingerprint_string = JSON.stringify(fingerprint_data);
    const hash = SHA256(fingerprint_string);
    this.cachedDeviceFingerprint = hash.toString(enc.Hex);
    return this.cachedDeviceFingerprint;
  }

  /**
   * Generate security headers
   */
  private generateSecurityHeaders(): any {
    return {
      'X-Client-Version': '1.0.0',
      'X-Request-ID': this.generateRequestId(),
      'X-Timestamp': Date.now().toString(),
      'X-Security-Level': 'high'
    };
  }

  /**
   * Get secure HTTP headers
   */
  private getSecureHeaders(): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
      'X-Client-Type': 'admin-frontend',
      'X-Security-Version': '2.0',
      'X-Screen-Resolution': `${screen.width}x${screen.height}`,
      'X-Timezone': Intl.DateTimeFormat().resolvedOptions().timeZone,
      'Accept-Language': navigator.language
    });
  }

  /**
   * Generate unique request ID
   */
  private generateRequestId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }

  /**
   * Start session monitoring
   */
  private startSessionMonitoring(): void {
    // Only start session monitoring in browser environment
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    
    // Clear existing timer
    if (this.sessionTimer) {
      clearInterval(this.sessionTimer);
    }

    // Set session timeout
    this.sessionTimer = setTimeout(() => {
      this.handleSessionTimeout();
    }, this.SESSION_TIMEOUT);

    // Monitor user activity
    this.monitorUserActivity();
  }

  /**
   * Monitor user activity to extend session
   */
  private monitorUserActivity(): void {
    // Only monitor user activity in browser environment
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    
    if (this.activityAbortController) {
      this.activityAbortController.abort();
    }
    this.activityAbortController = new AbortController();

    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
    
    const resetTimer = () => {
      if (this.sessionTimer) {
        clearTimeout(this.sessionTimer);
        this.sessionTimer = setTimeout(() => {
          this.handleSessionTimeout();
        }, this.SESSION_TIMEOUT);
      }
    };

    const onActivity = () => {
      const now = Date.now();
      if (now - this.activityLastResetAt < this.activityThrottleMs) return;
      this.activityLastResetAt = now;
      resetTimer();
    };

    const options: AddEventListenerOptions = {
      capture: true,
      passive: true,
      signal: this.activityAbortController.signal,
    };

    events.forEach((event) => {
      document.addEventListener(event, onActivity, options);
    });
  }

  /**
   * Handle session timeout
   */
  private handleSessionTimeout(): void {
    this.secureEncryption.clearEncryptionData();
    
    if (this.sessionTimer) {
      clearTimeout(this.sessionTimer);
    }
    if (this.activityAbortController) {
      this.activityAbortController.abort();
      this.activityAbortController = null;
    }

    this.authState.next(false);
    this.sessionExpiredService.handleSessionExpired();
  }

  /**
   * Secure logout
   */
  secureLogout(): void {
    // Clear all sensitive data
    this.secureEncryption.clearEncryptionData();
    this.browserStorage.clearSession();
    
    // Clear timers
    if (this.sessionTimer) {
      clearTimeout(this.sessionTimer);
    }
    if (this.activityAbortController) {
      this.activityAbortController.abort();
      this.activityAbortController = null;
    }

    // Update auth state
    this.authState.next(false);

    // Redirect to login
    this.router.navigate(['/login']);
  }

  /**
   * Check existing session
   */
  private checkExistingSession(): void {
    const loggedIn = this.browserStorage.getSessionItem('loggedIn');
    const loginTime = this.browserStorage.getSessionItem('loginTime');

    if (loggedIn === 'true' && loginTime) {
      const sessionAge = Date.now() - parseInt(loginTime);
      
      if (sessionAge < this.SESSION_TIMEOUT) {
        this.authState.next(true);
        this.startSessionMonitoring();
      } else {
        this.sessionExpiredService.handleSessionExpired();
      }
    }
  }

  /**
   * Get authentication state observable
   */
  getAuthState(): Observable<boolean> {
    return this.authState.asObservable();
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return this.authState.value;
  }

  /**
   * Get session age
   */
  private getSessionAge(): number {
    const loginTime = this.browserStorage.getSessionItem('loginTime');
    return loginTime ? Date.now() - parseInt(loginTime) : 0;
  }
}
